import { useMutation, useQuery, ApolloQueryResult, FetchResult } from '@apollo/client';
import { useContext } from 'react';

import { HookResult } from './apolloClient';
import { Post, PostPayload } from '@/types/post';
import { Section } from '@/types/Section';
import { CoursePageContext } from '@/pages/learning/CourseDetails/CoursePageContext';
import {
  CreatePostDocument,
  CreateSectionDocument,
  DeletePostDocument,
  GetPostDocument,
  ToggleActionDocument,
  UpdatePostDocument,
  UpdateSectionDocument,
} from '@/store/v2';

interface PostResponse extends HookResult {
  post?: Post;
  refetch: (variables?: { path?: string; postId?: number }) => Promise<ApolloQueryResult<{ getPost: Post }>>;
}

export const usePost = (path: string[], postId: number, skip = false): PostResponse => {
  const { data, error, loading, refetch, networkStatus } = useQuery<{ getPost: Post }>(GetPostDocument, {
    variables: { path, postId: postId },
    skip,
  });

  return {
    post: data?.getPost,
    error,
    loading,
    refetch,
    networkStatus,
  };
};

export const useCreateSection = (): ((
  path: string[],
  name: string,
  title?: string,
  description?: string,
) => Promise<FetchResult<{ createSection: Section }>>) => {
  const [create] = useMutation(CreateSectionDocument);

  return (path: string[], name: string, title?: string, description?: string) =>
    create({
      variables: {
        payload: {
          path,
          name,
          title,
          description,
        },
      },
    });
};

export const useUpdateSection = (): ((
  path: string[],
  title?: string,
  description?: string,
) => Promise<FetchResult<{ updateSection: Section }>>) => {
  const [update] = useMutation(UpdateSectionDocument);

  return (path: string[], title?: string, description?: string) =>
    update({
      variables: {
        payload: {
          path,
          title,
          description,
        },
      },
    });
};

export const useCreatePost = (): ((path: string[], payload: PostPayload) => Promise<FetchResult<{ createPost: Post }>>) => {
  const context = useContext(CoursePageContext);

  const [create] = useMutation(CreatePostDocument);

  return (path: string[], postPayload: PostPayload) => {
    const payload = {
      title: postPayload.title,
      content: postPayload.content,
      pid: postPayload.pid || null,
      originProductId: context.productId,
      media: postPayload.media || null,
      sticky: Boolean(postPayload.sticky),
      publishDate: postPayload.publishDate || null,
      shouldSendNotification: postPayload.shouldSendNotification,
    };
    if (postPayload.isReflectionSection) {
      delete payload.shouldSendNotification;
    }
    return create({
      variables: {
        path,
        payload,
      },
    });
  };
};

export const useUpdatePost = (): {
  loading: boolean;
  updatePostAction: (path: string[], postId: number, payload: PostPayload) => Promise<FetchResult<{ updatePost: Post }>>;
} => {
  const [update, { loading }] = useMutation(UpdatePostDocument);

  return {
    loading,
    updatePostAction: (path: string[], postId: number, payload: PostPayload) =>
      update({
        variables: {
          path,
          postId,
          payload: {
            title: payload.title,
            content: payload.content,
            media: payload.media ? { url: payload.media.url, type: payload.media.type } : undefined, // Return undefined so graphql knows not to update field
            publishDate: payload.publishDate,
            sticky: payload.sticky,
          },
        },
      }),
  };
};

export const useDeletePost = (): ((path: string[], postId: number) => Promise<FetchResult<{ deletePost: Post }>>) => {
  const [deletePost] = useMutation(DeletePostDocument);

  return (path: string[], postId: number) => deletePost({ variables: { path, postId } });
};

export const usePostAction = (): ((
  path: string[],
  postId: number,
  action: string,
  payload?: { scalar: number },
) => Promise<FetchResult<{ toggleAction: Record<string, unknown> }>>) => {
  const [toggleAction] = useMutation(ToggleActionDocument);

  return (path: string[], postId: number, action: string, payload?: { scalar: number }) =>
    toggleAction({
      variables: { path, postId, action, payload },
    });
};
